import { useMemo, useState } from 'react';
import { t } from '@hana/i18n';
import '../strings.i18n';
import { Link } from '@tanstack/react-router';
import { IconArrowLeft, IconBell, IconLayoutSidebarRight, IconPlus, IconRefresh } from '@tabler/icons-react';
import { Button, Segmented, StateView, useToast, type ScreenState } from '@hana/ui';
import { HeaderTool, Page, PageHeader } from '@hana/shell';
import { AUDIT, BOXES, LIC, USERS, initials, upn, type Tone } from './m365-data';
import s from './m365.module.css';

// Mint-M365: màn cấu hình tích hợp Microsoft 365 (Quản trị › Tích hợp). Dữ liệu mẫu, không gọi Graph, không hiện mật khẩu; các nút chỉ báo toast.
// Mới dựng tab Người dùng (design-specs/Mint-M365.md) + Hộp mail chung + Nhật ký quản trị; License / OneDrive / Ảnh đại diện làm ở I-06c.

type Tab = 'users' | 'shared' | 'lic' | 'od' | 'photo' | 'audit';
type Filter = 'all' | 'diff' | 'mfa' | 'photo';

const TITLE: Record<Tab, string> = { users: 'Người dùng', shared: 'Hộp mail chung', lic: 'License', od: 'OneDrive', photo: 'Ảnh đại diện', audit: 'Nhật ký quản trị' };

function readState(): ScreenState {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : 'ready';
}

const TONE: Record<Tone, string> = { ok: s.cOk!, warn: s.cWarn!, err: s.cErr!, mute: s.cMute! };

function Av({ un, name, sub, ring }: { un: string; name: string; sub: string; ring?: boolean }) {
  return (
    <div className={s.person}>
      <span className={s.av} aria-hidden="true" style={ring ? { borderColor: 'var(--bd)' } : undefined}>{initials(un)}</span>
      <div className={s.ell}>
        <div className={`${s.ell} ${s.name}`}>{name}</div>
        <div className={`${s.ell} ${s.sub}`}>{sub}</div>
      </div>
    </div>
  );
}

export function M365Page() {
  const state = useMemo(readState, []);
  const toast = useToast();
  const [tab, setTab] = useState<Tab>('users');
  const [dense, setDense] = useState(false);
  const [filter, setFilter] = useState<Filter>('all');
  const [box, setBox] = useState(BOXES[0]![0]);
  const [dock, setDock] = useState(false);
  const noWrite = (msg: string) => () => toast({ msg });

  const diff = USERS.filter((u) => u.flags.some((f) => f[1] === 'err' || f[0].startsWith('Chưa có')));
  const mfa = USERS.filter((u) => !u.mfa && u.m365 === 'on');
  const photo = USERS.filter((u) => u.photo !== 'both');
  const kpis: [Filter, string, number, string][] = [['all', 'Tài khoản', USERS.length, 'var(--tx)'], ['diff', 'Lệch app ↔ M365', diff.length, 'var(--err)'], ['mfa', 'Chưa bật MFA', mfa.length, 'var(--warn)'], ['photo', 'Ảnh chưa khớp', photo.length, 'var(--tx2)']];
  const rows = filter === 'all' ? USERS : filter === 'diff' ? diff : filter === 'mfa' ? mfa : photo;
  const acc = (a: string) => <span className={`${s.chip} ${a === 'on' ? s.cOk : a === 'off' ? s.cErr : s.cMute}`}>{a === 'on' ? 'Mở' : a === 'off' ? 'Khoá' : '—'}</span>;
  const b = BOXES.find((x) => x[0] === box) ?? BOXES[0]!;

  const tabs: { key: Tab; label: string; n?: number }[] = [
    { key: 'users', label: 'Người dùng', n: USERS.length }, { key: 'shared', label: 'Hộp mail chung', n: BOXES.length }, { key: 'lic', label: 'License' },
    { key: 'od', label: 'OneDrive' }, { key: 'photo', label: 'Ảnh đại diện' }, { key: 'audit', label: 'Nhật ký quản trị', n: AUDIT.length },
  ];
  const acts =
    tab === 'users'
      ? [['Đồng bộ tên hàng loạt', 'Đẩy tên hiển thị HANA Ezy lên M365 cho mọi người lệch'], ['Đồng bộ thuộc tính', 'Đẩy phòng, chức danh, quản lý trực tiếp lên M365']]
      : tab === 'shared'
        ? [['Quét người được cấp', 'Quét toàn bộ hộp chung để biết ai được cấp gì'], ['Đổi ảnh hộp', 'Đổi ảnh hộp chung']]
        : [];

  return (
    <>
      <PageHeader
        title="Microsoft 365"
        lead={
          <Link to="/kit" className={s.back} aria-label="Về Quản trị hệ thống" title="Quản trị hệ thống">
            <IconArrowLeft size={17} stroke={2.2} aria-hidden="true" />
          </Link>
        }
        actions={
          <>
            <Segmented kind="toggle" label="Mật độ" value={dense ? 'compact' : 'std'} onChange={(k) => setDense(k === 'compact')} items={[{ key: 'std', label: 'Chuẩn' }, { key: 'compact', label: 'Gọn' }]} />
            <HeaderTool label="Thông báo · 3 mới" icon={IconBell} n={3} />
            <HeaderTool label="Cột phải: cần chú ý" icon={IconLayoutSidebarRight} active={dock} onClick={() => setDock(!dock)} />
            <Button variant="primary" icon={<IconPlus size={16} aria-hidden="true" />} onClick={noWrite('Chưa mở thêm tài khoản M365 ở bản này')}>{t('kit.x.taiKhoanM365')}</Button>
          </>
        }
      />
      <Page kind="table">
        <StateView
          state={state === 'loading' ? 'ready' : state}
          title={state === 'noperm' ? 'Quản trị Microsoft 365 chỉ mở cho ghế có cờ ADMIN' : state === 'empty' ? 'Chưa kết nối Microsoft 365' : 'Không đọc được dữ liệu từ Microsoft 365.'}
          desc={state === 'noperm' ? 'Chỉ xem.' : undefined}
          action={state === 'error' ? <Button icon={<IconRefresh size={16} aria-hidden="true" />} onClick={noWrite('Đang đọc lại dữ liệu từ Microsoft 365…')}>{t('kit.x.thuLai')}</Button> : undefined}
        >
          <section className={s.card} aria-label={TITLE[tab]}>
            <div className={s.top}>
              <div className={s.topRow}>
                <Segmented<Tab> label="Mục M365" value={tab} onChange={(k) => { setTab(k); setFilter('all'); }} items={tabs} />
                <div className={s.acts}>
                  {acts.map(([k, tip]) => (
                    <Button key={k} title={tip} onClick={noWrite(`Chưa mở "${k}" ở bản này`)}>{k}</Button>
                  ))}
                </div>
              </div>
              {tab === 'users' && (
                <div className={s.kpis}>
                  {kpis.map(([key, k, v, c]) => (
                    <button key={key} type="button" className={s.kpi} aria-pressed={filter === key} onClick={() => setFilter(key)}>
                      <div className={s.kpiK}>{k}</div>
                      <div className={s.kpiV} style={{ color: c }}>{v}</div>
                    </button>
                  ))}
                </div>
              )}
              {tab === 'users' && filter !== 'all' && <div className={`${s.note} ${s.noteP}`}>{`Đang lọc theo ô số · bấm lại "Tài khoản" để bỏ lọc · ${rows.length} khớp`}</div>}
            </div>

            {tab === 'users' && (
              <div className={s.tbl} role="table" aria-label="Người dùng">
                <div role="row" className={`${s.row} ${s.head} ${s.gUsers}`}>
                  {['Người', 'Phòng · ghế', 'App', 'M365', 'License', 'Cờ lệch', ''].map((h, i) => <span key={i} role="columnheader">{h}</span>)}
                </div>
                {rows.map((u) => (
                  <div key={u.un} role="row" className={`${s.row} ${s.gUsers} ${dense ? s.dense : ''}`}>
                    <Av un={u.un} name={`${u.un} · ${u.fn}`} sub={u.m365 === 'none' ? 'chưa có tài khoản M365' : upn(u.un)} ring={u.photo !== 'both'} />
                    <span className={`${s.ell} ${s.t2}`}>{u.seat}</span>
                    {acc(u.app)}
                    {acc(u.m365)}
                    <span className={`${s.ell} ${u.lic.length ? s.t2 : s.lb}`}>{u.lic.length ? u.lic.map((l) => LIC[l]!.replace('M365 ', '')).join(' · ') : '—'}</span>
                    {u.flags.length ? <div className={s.chips}>{u.flags.map(([k, t]) => <span key={k} className={`${s.chip} ${TONE[t]}`}>{k}</span>)}</div> : <span className={s.okTx}>{t('kit.x.khop')}</span>}
                    <div className={s.btns}>
                      <button type="button" className={s.rb} aria-label={`Đặt lại mật khẩu M365 của ${u.un}`} title={`Đặt lại mật khẩu M365 của ${u.un}`} onClick={noWrite('Chưa mở đặt lại mật khẩu ở bản này')}>{t('kit.x.matKhau')}</button>
                      <button type="button" className={`${s.rb} ${s.ghost}`} aria-label={`Rule hộp thư của ${u.un}`} title={`Rule hộp thư của ${u.un}`} onClick={noWrite('Chưa mở rule hộp thư ở bản này')}>{t('kit.x.hopThu')}</button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {tab === 'shared' && (
              <div className={s.split}>
                <nav className={s.side} aria-label="Hộp chung">
                  {BOXES.map((x) => (
                    <button key={x[0]} type="button" className={s.sideB} aria-current={x[0] === box ? 'page' : undefined} onClick={() => setBox(x[0])}>
                      <span className={s.av} aria-hidden="true">{x[0].slice(0, 2).toUpperCase()}</span>
                      <span className={s.ell}><span className={`${s.ell} ${s.name}`}>{x[0]}hanalogisticsvn.com</span><span className={`${s.ell} ${s.sub}`}>{`${x[1]} · ${x[2].length} người`}</span></span>
                    </button>
                  ))}
                </nav>
                <div className={s.tbl} role="table" aria-label="Hộp mail chung">
                  <div role="row" className={`${s.row} ${s.head} ${s.gBox}`}>{['Thành viên', 'Ghế', 'Full Access', 'Send As'].map((h) => <span key={h} role="columnheader">{h}</span>)}</div>
                  {b[2].map(([un, fa, sa]) => {
                    const u = USERS.find((x) => x.un === un);
                    const left = un === 'AnNPH';
                    return (
                      <div key={un} role="row" className={`${s.row} ${s.gBox} ${dense ? s.dense : ''}`}>
                        <Av un={un} name={un + (u ? ` · ${u.fn}` : '')} sub={left ? 'đã khoá đăng nhập · nên thu hồi' : upn(un)} />
                        <span className={`${s.ell} ${s.t2}`}>{u ? u.seat : 'CSD · CSD-BKG'}</span>
                        <span className={s.t2}>{fa ? 'Có' : '—'}</span>
                        <span className={s.t2}>{sa ? 'Có' : '—'}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {tab === 'audit' && (
              <div className={s.tbl} role="table" aria-label="Nhật ký quản trị">
                <div role="row" className={`${s.row} ${s.head} ${s.gAud}`}>{['Lúc', 'Người', 'Việc', 'Đối tượng', 'Hệ'].map((h) => <span key={h} role="columnheader">{h}</span>)}</div>
                {AUDIT.map((a) => (
                  <div key={a[0]} role="row" className={`${s.row} ${s.gAud} ${dense ? s.dense : ''}`}>
                    <span className={s.t2}>{a[0]}</span><span className={s.name}>{a[1]}</span><span className={s.ell}>{a[2]}</span><span className={`${s.ell} ${s.t2}`}>{a[3]}</span><span className={s.t2}>{a[4]}</span>
                  </div>
                ))}
              </div>
            )}

            {(tab === 'lic' || tab === 'od' || tab === 'photo') && <div className={s.none}>{t('kit.x.mucNayDung')}</div>}

            <div className={s.foot}>
              {tab === 'users' ? `${rows.length} / ${USERS.length} tài khoản · 37 đang làm` : tab === 'shared' ? `${b[2].length} thành viên · thay đổi có hiệu lực trên Exchange sau 5–30 phút` : tab === 'audit' ? `${AUDIT.length} thao tác gần nhất` : ''}
            </div>
          </section>
        </StateView>
      </Page>
    </>
  );
}
